// Localized storefront display.
import { tr, useStoreLanguage } from '@/i18n';
import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from '@/i18n/router';
import { useSEO } from '@/utils/seoHelpers';
import useScrollAnimation from '@/hooks/useScrollAnimation';

const quickLinks = [
  { label: 'Inicio', to: '/', icon: 'ri-home-4-line', desc: 'Volver a la página principal' },
  { label: 'Productos', to: '/products', icon: 'ri-shopping-bag-line', desc: 'Ver todas las especias y hierbas' },
  { label: 'Recetas', to: '/recipes', icon: 'ri-restaurant-line', desc: 'Inspiración para cocinar' },
  { label: 'Blog', to: '/blog', icon: 'ri-article-line', desc: 'Artículos sobre especias' },
  { label: 'Ofertas', to: '/offers', icon: 'ri-percent-line', desc: 'Productos con descuento' },
  { label: 'Contacto', to: '/contact', icon: 'ri-mail-line', desc: 'Habla con nosotros' },
];

const popularProducts = [
  { label: 'Cúrcuma Orgánica Molida', to: '/product/curcuma-organica-molida' },
  { label: 'Mezcla de Hierbas Orgánicas', to: '/product/mezcla-de-hierbas-organicas' },
  { label: 'Pimienta Negra Premium', to: '/product/pimienta-negra-premium' },
  { label: 'Canela de Ceilán Premium', to: '/product/canela-de-ceilan-premium' },
];

export default function NotFound() {
  useStoreLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const [countdown, setCountdown] = useState(10);
  const contentAnim = useScrollAnimation({ threshold: 0.05 });

  useSEO({
    title: 'Página no encontrada (404) | Solsazón',
    description: 'Lo sentimos, la página que buscas no existe. Explora nuestros productos de especias y hierbas orgánicas.',
    noindex: true,
  });

  // Auto-redirect countdown
  useEffect(() => {
    if (countdown <= 0) {
      navigate('/');
      return;
    }
    const timer = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [countdown, navigate]);

  return (
    <div className="min-h-screen bg-stone-50 flex flex-col">
      {/* Main content */}
      <main className="flex-1 flex flex-col items-center justify-center px-4 py-16">

        {/* Decorative background number */}
        <div className="relative w-full max-w-4xl mx-auto">
          <div
            className="absolute inset-0 flex items-center justify-center pointer-events-none select-none"
            aria-hidden="true"
          >
            <span className="text-[18rem] font-black text-stone-100 leading-none tracking-tighter">
              404
            </span>
          </div>

          {/* Content card */}
          <div
            ref={contentAnim.ref}
            className="relative z-10 text-center py-16 transition-all duration-700"
            style={{
              opacity: contentAnim.isVisible ? 1 : 0,
              transform: contentAnim.isVisible ? 'translateY(0)' : 'translateY(32px)',
            }}
          >
            {/* Icon */}
            <div className="w-20 h-20 flex items-center justify-center mx-auto mb-6 bg-forest-100 rounded-full">
              <i className="ri-map-pin-2-line text-4xl text-forest-600"></i>
            </div>

            <h1 className="font-playfair text-4xl md:text-5xl font-bold text-gray-900 mb-4">{tr("¡Ups! Página no encontrada")}</h1>
            <p className="font-inter text-lg text-gray-500 mb-2 max-w-lg mx-auto">{tr("La página")}{tr(' ')}
              <code className="font-mono text-sm bg-stone-200 text-gray-700 px-2 py-0.5 rounded">
                {location.pathname}
              </code>{tr(' ')}{tr("no existe o ha sido movida.")}</p>
            <p className="font-inter text-sm text-gray-400 mb-8">{tr("Redirigiendo al inicio en")}{tr(' ')}
              <span className="font-semibold text-forest-600">{tr(countdown)}</span>{tr(" segundos...")}</p>

            {/* Primary CTA */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mb-12">
              <Link
                to="/"
                className="inline-flex items-center px-8 py-3.5 bg-forest-600 hover:bg-forest-700 text-white font-inter font-semibold rounded-xl transition-all hover:scale-105 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-home-4-line mr-2"></i>{tr("Ir al Inicio")}</Link>
              <button
                onClick={() => window.history.back()}
                className="inline-flex items-center px-8 py-3.5 bg-white hover:bg-stone-50 text-gray-700 font-inter font-semibold rounded-xl border border-stone-200 transition-all hover:scale-105 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-arrow-left-line mr-2"></i>{tr("Volver atrás")}</button>
            </div>

            {/* Progress bar for countdown */}
            <div className="w-48 h-1 bg-stone-200 rounded-full mx-auto mb-12 overflow-hidden">
              <div
                className="h-full bg-forest-500 rounded-full transition-all duration-1000 ease-linear"
                style={{ width: `${(countdown / 10) * 100}%` }}
              />
            </div>
          </div>
        </div>

        {/* Quick links grid */}
        <div className="w-full max-w-4xl mx-auto">
          <p className="font-inter text-sm font-semibold text-gray-400 uppercase tracking-widest text-center mb-6">{tr("¿A dónde quieres ir?")}</p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {tr(quickLinks.map((link, index) => (
              <Link
                key={link.to}
                to={link.to}
                className="group flex items-center space-x-3 bg-white hover:bg-forest-50 border border-stone-100 hover:border-forest-200 rounded-xl p-4 transition-all duration-300 cursor-pointer"
                style={{
                  animationDelay: `${index * 60}ms`,
                }}
              >
                <div className="w-10 h-10 flex items-center justify-center bg-forest-100 group-hover:bg-forest-200 rounded-lg flex-shrink-0 transition-colors">
                  <i className={`${link.icon} text-forest-600 text-lg`}></i>
                </div>
                <div className="min-w-0">
                  <p className="font-inter text-sm font-semibold text-gray-900 group-hover:text-forest-700 transition-colors">
                    {tr(link.label)}
                  </p>
                  <p className="font-inter text-xs text-gray-400 truncate">{tr(link.desc)}</p>
                </div>
              </Link>
            )))}
          </div>
        </div>

        {/* Popular products */}
        <div className="w-full max-w-4xl mx-auto mt-10">
          <p className="font-inter text-sm font-semibold text-gray-400 uppercase tracking-widest text-center mb-4">{tr("Productos populares")}</p>
          <div className="flex flex-wrap justify-center gap-2">
            {tr(popularProducts.map((p) => (
              <Link
                key={p.to}
                to={p.to}
                className="inline-flex items-center px-4 py-2 bg-white hover:bg-forest-50 border border-stone-200 hover:border-forest-300 text-gray-700 hover:text-forest-700 font-inter text-sm rounded-full transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-leaf-line mr-1.5 text-forest-500"></i>
                {tr(p.label)}
              </Link>
            )))}
          </div>
        </div>

        {/* Search suggestion */}
        <div className="w-full max-w-md mx-auto mt-10 text-center">
          <p className="font-inter text-sm text-gray-400 mb-3">{tr("¿Buscas algo específico?")}</p>
          <Link
            to="/products"
            className="inline-flex items-center space-x-2 w-full px-5 py-3 bg-white border border-stone-200 rounded-xl text-gray-500 hover:border-forest-300 hover:text-forest-600 transition-all cursor-pointer group"
          >
            <i className="ri-search-line text-lg group-hover:text-forest-600 transition-colors"></i>
            <span className="font-inter text-sm">{tr("Buscar en el catálogo de especias...")}</span>
          </Link>
        </div>
      </main>

      {/* Footer strip */}
      <footer className="py-6 border-t border-stone-200 bg-white">
        <div className="max-w-4xl mx-auto px-4 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="font-inter text-sm text-gray-400">{tr("© 2026 Solsazón · Especias y Hierbas Orgánicas desde Almería")}</p>
          <div className="flex items-center space-x-4 text-sm font-inter">
            <Link to="/contact" className="text-gray-400 hover:text-forest-600 transition-colors cursor-pointer">{tr("Contacto")}</Link>
            <span className="text-gray-200">·</span>
            <Link to="/faq" className="text-gray-400 hover:text-forest-600 transition-colors cursor-pointer">{tr("FAQ")}</Link>
            <span className="text-gray-200">·</span>
            <Link to="/sitemap" className="text-gray-400 hover:text-forest-600 transition-colors cursor-pointer">{tr("Sitemap")}</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
